import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import api, { getError } from "@/api";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import StockPage from "@/pages/StockPage";
import StockAvailablePage from "@/pages/StockAvailablePage";
import LocationsPage from "@/pages/LocationsPage";
import WarehouseDocumentsPage from "@/pages/WarehouseDocumentsPage";
import MovementsPage from "@/pages/MovementsPage";
import FilesPage from "@/pages/FilesPage";
import SettingsPage from "@/pages/SettingsPage";
import SalesPage from "@/pages/SalesPage";
import AccountPage from "@/pages/AccountPage";
import StaffPage from "@/pages/StaffPage";
import ProductCataloguePage from "@/pages/ProductCataloguePage";
import ProductProfessionalPage from "@/pages/ProductProfessionalPage";
import CompanyLogoPage from "@/pages/CompanyLogoPage";
import CompanySettingsPage from "@/pages/CompanySettingsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import SettingsCombinedPage from "@/pages/SettingsCombinedPage";
import SettingsHomePage from "@/pages/SettingsHomePage";
import DataMaintenancePage from "@/pages/DataMaintenancePage";
import UserAccessManagementPage from "@/pages/UserAccessManagementPage";
import StockMovementsHubPage from "@/pages/StockMovementsHubPage";
import ProductCatalogueHubPage from "@/pages/ProductCatalogueHubPage";
import MovementReportsPage from "@/pages/MovementReportsPage";
import StockReportsPage from "@/pages/StockReportsPage";
import DataExchangePage from "@/pages/DataExchangePage";
import DeliveryPage from "@/pages/DeliveryPage";
import CommissionManagementPage from "@/pages/CommissionManagementPage";

function AuthCallback({ onAuthenticated }) {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace("#", "")).get("session_id");
    api.post("/auth/google/session", null, { params: { session_id: sessionId } }).then((response) => {
      onAuthenticated(response.data);
      navigate("/dashboard", { replace: true });
    }).catch((error) => {
      toast.error(getError(error));
      navigate("/connexion", { replace: true });
    });
  }, [location.hash, navigate, onAuthenticated]);
  return <div className="auth-callback" data-testid="google-auth-callback">Connexion sécurisée en cours…</div>;
}

function AppRoutes() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [bootstrap, setBootstrap] = useState(false);

  useEffect(() => {
    if (location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    api.get("/auth/me").then((response) => setUser(response.data)).catch(async () => {
      try {
        const response = await api.get("/auth/has-users");
        setBootstrap(!response.data.has_users);
      } catch {
        setBootstrap(false);
      }
    }).finally(() => setChecking(false));
  }, [location.hash]);

  useEffect(() => {
    const updateUser = (event) => setUser((current) => ({ ...current, ...event.detail }));
    window.addEventListener("ecom-profile-updated", updateUser);
    return () => window.removeEventListener("ecom-profile-updated", updateUser);
  }, []);

  if (location.hash?.includes("session_id=")) return <AuthCallback onAuthenticated={setUser} />;
  if (checking) return <div className="app-loading" data-testid="authentication-check">Vérification de votre accès…</div>;
  const protectedPage = (element) => user ? <AppShell user={user} onLogout={() => setUser(null)}>{element}</AppShell> : <Navigate to="/connexion" replace />;
  return <Routes>
    <Route path="/connexion" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage bootstrap={bootstrap} onAuthenticated={setUser} />} />
    <Route path="/dashboard" element={protectedPage(<DashboardPage user={user} />)} />
    <Route path="/stock" element={protectedPage(<StockPage user={user} />)} />
    <Route
      path="/stock-disponible"
      element={protectedPage(<StockAvailablePage user={user} />)}
    />
    <Route path="/stock-mouvements" element={protectedPage(<StockMovementsHubPage />)} />
    <Route path="/catalogue" element={protectedPage(<ProductCataloguePage />)} />
    <Route path="/produit-catalogue" element={protectedPage(<ProductCatalogueHubPage />)} />
    <Route path="/produits" element={protectedPage(<ProductProfessionalPage />)} />
    <Route path="/parametres/logo" element={protectedPage(<CompanyLogoPage />)} />
    <Route path="/depots" element={protectedPage(<LocationsPage user={user} />)} />
    <Route path="/documents-depot" element={protectedPage(<WarehouseDocumentsPage />)} />
    <Route path="/mouvements" element={protectedPage(<MovementsPage />)} />
    <Route path="/livraisons" element={protectedPage(<DeliveryPage user={user} />)} />
    <Route path="/rapports" element={protectedPage(<MovementReportsPage />)} />
    <Route path="/rapports-stock" element={protectedPage(<StockReportsPage />)} />
    <Route path="/entrees" element={<Navigate to="/mouvements" replace />} />
    <Route path="/ventes" element={protectedPage(<SalesPage />)} />
    <Route path="/fichiers" element={protectedPage(<FilesPage />)} />
    <Route path="/parametres" element={protectedPage(<SettingsHomePage />)} />
    <Route path="/parametres/utilisateurs" element={protectedPage(<UserAccessManagementPage />)} />
    <Route path="/parametres/commissions" element={protectedPage(<CommissionManagementPage />)} />
    <Route path="/parametres/entreprise" element={protectedPage(<CompanySettingsPage />)} />
    <Route path="/parametres/donnees" element={protectedPage(<DataMaintenancePage />)} />
    <Route path="/parametres/echange-donnees" element={protectedPage(<DataExchangePage />)} />
    <Route path="/profil" element={protectedPage(<AccountPage />)} />
    <Route path="/personnel" element={protectedPage(<StaffPage />)} />
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>;
}

function App() {
  useEffect(() => {
    api.get("/branding").then((response) => {
      document.title = "Gestion Stock Informatisée d'Ecom Guinée";
      const favicon = document.getElementById("app-favicon");
      if (favicon && response.data.logo_url) favicon.href = response.data.logo_url;
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    }
  }, []);
  useEffect(() => {
    const handleTagEnter = (event) => {
      if (event.key !== "Enter" || event.target?.dataset?.testid !== "product-tag-input") return;
      event.preventDefault();
      const form = event.target.closest("form, .tag-editor, .stack-form");
      const button = form?.querySelector('[data-testid="add-product-tag-button"]') || document.querySelector('[data-testid="add-product-tag-button"]');
      button?.click();
    };
    document.addEventListener("keydown", handleTagEnter);
    return () => document.removeEventListener("keydown", handleTagEnter);
  }, []);
  return <BrowserRouter><AppRoutes /><Toaster position="top-right" richColors /></BrowserRouter>;
}

export default App;
